import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { Link, useSearch } from "@tanstack/react-router";

// Shown when a signed-in user follows a deep link to an org their active account
// isn't a member of. We used to silently swap their active org and bounce them
// to their own dashboard, which read as "the link points at the wrong org".
// In practice the common cause is a wrong-account mismatch (e.g. someone signed
// in as their personal identity opening a link for their work org), so we name
// the signed-in email to make that obvious rather than only offering "get
// invited". The target org id (carried through from the guard) is surfaced too
// so they can quote it when asking to be invited — its name isn't resolvable
// here since the viewer isn't a member.
export function NoAccessPage() {
  const { data: session } = useSession();
  const email = session?.user?.email;
  const { org } = useSearch({ strict: false }) as { org?: string };

  return (
    <div className="flex min-h-screen flex-1 flex-col items-center justify-center p-6">
      <div className="w-full max-w-md flex flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          You don't have access to this organization
        </h1>
        <p className="text-muted-foreground">
          The account you're signed in as
          {email ? (
            <>
              {" "}(<span className="font-medium text-foreground">{email}</span>)
            </>
          ) : null}{" "}
          isn't a member of this organization. If you have another account that
          is, sign in with that one — or ask one of its owners to invite you.
        </p>
        {org ? (
          <p className="text-xs text-muted-foreground">
            Organization ID:{" "}
            <span className="font-mono text-foreground">{org}</span>
          </p>
        ) : null}
        <Button asChild variant="outline">
          <Link to="/">Go to my dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
